import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { resolveMediaAssetUrlCandidates } from '../../../utils/mediaAssetUrl';

const formatApiError = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const messages = Object.values(payload.errors).find((entry) => Array.isArray(entry) && entry.length > 0);
    if (messages) {
      return messages.join(' ');
    }
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  return fallback;
};

const MediaLibraryPickerModal = ({ open, onClose, onSelect }) => {
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAssets = useCallback(
    async (page = 1) => {
      if (!open) return;

      setLoading(true);
      setError('');

      try {
        const { data } = await axios.get('/admin/media-library', {
          params: {
            page,
            search: searchTerm || undefined,
            per_page: 18,
          },
        });

        setAssets(Array.isArray(data?.data) ? data.data : []);
        setMeta({
          current_page: Number(data?.current_page || 1),
          last_page: Number(data?.last_page || 1),
          total: Number(data?.total || 0),
        });
      } catch (requestError) {
        setError(formatApiError(requestError, 'Failed to load media library.'));
      } finally {
        setLoading(false);
      }
    },
    [open, searchTerm]
  );

  useEffect(() => {
    if (!open) return;
    loadAssets(1);
  }, [open, loadAssets]);

  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearchTerm('');
      setAssets([]);
      setMeta({ current_page: 1, last_page: 1, total: 0 });
      setError('');
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="media-picker-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Select image from media library"
      onClick={onClose}
    >
      <div className="card media-picker-modal__panel" onClick={(event) => event.stopPropagation()}>
        <div className="media-picker-modal__header">
          <div>
            <h3>Select Image from Media Library</h3>
            <p className="text-muted">Choose an uploaded image and apply it to this field.</p>
          </div>
          <button className="btn btn-muted" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <form
          className="media-library__search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearchTerm(searchInput.trim());
          }}
        >
          <input
            className="input"
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by image name"
          />
          <button className="btn btn-muted" type="submit">
            Search
          </button>
          <button
            className="btn btn-muted"
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearchTerm('');
            }}
          >
            Clear
          </button>
        </form>

        {error && <div className="notice notice-danger">{error}</div>}

        {loading ? (
          <div className="card">Loading media library...</div>
        ) : assets.length ? (
          <>
            <div className="media-library__grid">
              {assets.map((asset) => {
                const urlCandidates = resolveMediaAssetUrlCandidates(asset);
                const resolvedUrl = urlCandidates[0] || asset.url;

                return (
                  <article key={asset.id} className="card media-library__item media-picker-modal__item">
                    <div className="media-library__preview-wrap">
                      <img
                        src={resolvedUrl}
                        alt={asset.original_name || 'Image asset'}
                        className="media-library__preview"
                        loading="lazy"
                        onError={(event) => {
                          const nextIndex = Number(event.currentTarget.dataset.urlIndex || '0') + 1;
                          if (nextIndex >= urlCandidates.length) {
                            return;
                          }
                          event.currentTarget.dataset.urlIndex = String(nextIndex);
                          event.currentTarget.src = urlCandidates[nextIndex];
                        }}
                      />
                    </div>
                    <div className="media-library__meta">
                      <strong title={asset.original_name}>{asset.original_name}</strong>
                    </div>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        onSelect(resolvedUrl);
                        onClose();
                      }}
                    >
                      Use Image
                    </button>
                  </article>
                );
              })}
            </div>

            <div className="media-picker-modal__footer">
              <span className="text-muted">
                Page {meta.current_page} of {meta.last_page} · {meta.total} image{meta.total === 1 ? '' : 's'}
              </span>
              <div style={{ display: 'flex', gap: '0.45rem' }}>
                <button
                  className="btn btn-muted"
                  type="button"
                  disabled={meta.current_page <= 1}
                  onClick={() => loadAssets(meta.current_page - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-muted"
                  type="button"
                  disabled={meta.current_page >= meta.last_page}
                  onClick={() => loadAssets(meta.current_page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="card empty-state">
            <h3>No images found</h3>
            <p className="text-muted">Upload images in Media Library first, then select them here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLibraryPickerModal;
